import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	Box,
	Flex,
	Grid,
	GridItem,
	Text,
	Input,
	Select,
	Button,
	Badge,
	Progress,
	Tabs,
	TabList,
	Tab,
	TabPanels,
	TabPanel,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	TableContainer,
	Skeleton,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormLabel,
	FormControl,
	Divider,
	Link,
	Spinner,
	Tooltip,
	useToast,
	useDisclosure,
} from '@chakra-ui/react';

import {
	getRescueSummary,
	listRescuePrograms,
	getProgramStatus,
	listHypershared,
	queueBatchForProgram,
	queueHypersharedBatch,
	listRescueHistory,
	getBatchItems,
	retryFailedBatch,
	regenerateBatchItem,
	getDailyProgress,
	getBatchLiveStatus,
} from 'api/administrador/rescate';

const COST_PER_VIDEO_USD = 0.7;

const VOICE_PRESET_OPTIONS = [
	{ value: '', label: 'Por defecto (según audiencia)' },
	{ value: 'gonzalo', label: 'Gonzalo (masculina — narrador)' },
	{ value: 'salome', label: 'Dalia (femenina — narradora)' },
	{ value: 'bilingue', label: 'Bilingüe (español + inglés nativo)' },
];

const PROGRAM_TYPE_LABEL = {
	grade: 'Bachillerato',
	carrera_técnica: 'Carrera técnica',
	course: 'Curso',
};

const PROGRAM_TYPE_COLOR = {
	grade: 'blue',
	carrera_técnica: 'purple',
	course: 'orange',
};

// Categorías del selector principal. "hipercompartido" es el default por ser
// el de mayor impacto por USD gastado (cada video reconecta 30-80 lecciones
// distribuidas en múltiples programas).
const CATEGORY_OPTIONS = [
	{ value: 'hipercompartido', label: 'Prioritarios (reutilizables)' },
	{ value: 'grade', label: 'Bachillerato' },
	{ value: 'carrera_técnica', label: 'Carrera técnica' },
	{ value: 'course', label: 'Curso' },
];

const currency = (n) =>
	`USD $${Number(n || 0).toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

// Contexto compartido para "batches encolados en esta sesión". Se eleva al
// dashboard para que el LiveBatchStatusPanel se muestre encima de los tabs
// (visible tanto en Recuperar como en Historial), y para que un batch de
// regeneración lanzado desde el modal de Historial también quede trackeado
// con progreso live. Persiste en sessionStorage para sobrevivir F5.
const RescueBatchesContext = React.createContext(null);
const useRescueBatches = () => {
	const ctx = React.useContext(RescueBatchesContext);
	if (!ctx) throw new Error('useRescueBatches fuera de RescueBatchesContext.Provider');
	return ctx;
};

export default function RescateVideosDashboard() {
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = searchParams.get('tab') === 'historial' ? 1 : 0;

	const [activeBatches, setActiveBatches] = React.useState(() => {
		try {
			const stored = sessionStorage.getItem('rescate.activeBatches');
			return stored ? JSON.parse(stored) : [];
		} catch {
			return [];
		}
	});

	React.useEffect(() => {
		try {
			sessionStorage.setItem('rescate.activeBatches', JSON.stringify(activeBatches));
		} catch {
			/* no-op — modo incógnito */
		}
	}, [activeBatches]);

	const trackBatch = React.useCallback((batch) => {
		setActiveBatches((prev) => {
			if (prev.some((b) => b.batch_id === batch.batch_id)) return prev;
			return [batch, ...prev];
		});
	}, []);

	const dismissBatch = React.useCallback((batchId) => {
		setActiveBatches((prev) => prev.filter((b) => b.batch_id !== batchId));
	}, []);

	// Los tabs se registran como listeners para refrescar sus datos al
	// completarse un batch (KPIs, historial, chart). Un simple array de
	// callbacks es suficiente — no hay que orquestar orden.
	const completionListenersRef = React.useRef(new Set());
	const registerCompletionListener = React.useCallback((fn) => {
		completionListenersRef.current.add(fn);
		return () => completionListenersRef.current.delete(fn);
	}, []);
	const notifyCompletion = React.useCallback((batchId) => {
		for (const fn of completionListenersRef.current) {
			try {
				fn(batchId);
			} catch {
				/* no romper por un listener */
			}
		}
	}, []);

	const ctxValue = React.useMemo(
		() => ({
			activeBatches,
			trackBatch,
			dismissBatch,
			registerCompletionListener,
			notifyCompletion,
		}),
		[activeBatches, trackBatch, dismissBatch, registerCompletionListener, notifyCompletion],
	);

	return (
		<RescueBatchesContext.Provider value={ctxValue}>
		<Box p={{ base: '16px', md: '24px' }} maxW="1400px" mx="auto">
			<Flex justify="space-between" align="center" mb="16px">
				<Box>
					<Text fontSize="24px" fontWeight="700" color="gray.800">
						Rescate de Videos
					</Text>
					<Text fontSize="14px" color="gray.600">
						Restauración del material perdido en el incidente del 1 de julio.
					</Text>
				</Box>
			</Flex>

			{activeBatches.length > 0 && (
				<Flex direction="column" gap="8px" mb="16px">
					{activeBatches.map((b) => (
						<LiveBatchStatusPanel
							key={b.batch_id}
							batch={b}
							onDismiss={() => dismissBatch(b.batch_id)}
							onCompleted={() => notifyCompletion(b.batch_id)}
						/>
					))}
				</Flex>
			)}

			<Tabs
				index={activeTab}
				onChange={(idx) => {
					const next = idx === 1 ? 'historial' : 'recuperar';
					setSearchParams({ tab: next });
				}}
				colorScheme="purple"
				variant="line"
			>
				<TabList>
					<Tab fontWeight="600">Recuperar</Tab>
					<Tab fontWeight="600">Historial</Tab>
				</TabList>
				<TabPanels>
					<TabPanel px="0">
						<RecuperarTab />
					</TabPanel>
					<TabPanel px="0">
						<HistorialTab />
					</TabPanel>
				</TabPanels>
			</Tabs>
		</Box>
		</RescueBatchesContext.Provider>
	);
}

// ============================================================================
// Tab 1 — Recuperar
// ============================================================================

function RecuperarTab() {
	const { activeBatches, trackBatch, registerCompletionListener } = useRescueBatches();
	const [summary, setSummary] = React.useState(null);
	const [summaryLoading, setSummaryLoading] = React.useState(true);
	const [programs, setPrograms] = React.useState([]);
	const [programsLoading, setProgramsLoading] = React.useState(false);
	const [hypershared, setHypershared] = React.useState(null);
	// status por program key: 'idle' | 'loading' | 'ok' | 'error'
	const [rowStatus, setRowStatus] = React.useState({});
	const [rowData, setRowData] = React.useState({});
	const [rowError, setRowError] = React.useState({});
	const [rowSize, setRowSize] = React.useState({});
	const [rowBatch, setRowBatch] = React.useState({});
	const [category, setCategory] = React.useState('hipercompartido');
	const [search, setSearch] = React.useState('');
	const [sortBy, setSortBy] = React.useState('estudiantes_desc');
	const [modalProgram, setModalProgram] = React.useState(null);
	const modal = useDisclosure();
	const hypersharedModal = useDisclosure();
	const toast = useToast();

	const refreshHypershared = async () => {
		try {
			const hs = await listHypershared();
			setHypershared(hs);
		} catch (err) {
			// silencioso — solo afecta el conteo del modo prioritario
		}
	};

	// Al montar: solo summary + hypershared (rápidos). Programs se cargan lazy
	// cuando el usuario cambia el select a un tipo real.
	React.useEffect(() => {
		Promise.all([getRescueSummary(), listHypershared()])
			.then(([sum, hs]) => {
				setSummary(sum);
				setSummaryLoading(false);
				setHypershared(hs);
			})
			.catch((err) => {
				toast({
					title: 'Error al cargar el resumen',
					description: err.message,
					status: 'error',
				});
				setSummaryLoading(false);
			});
	}, [toast]);

	// Refrescar KPIs cuando un batch (de cualquier tab) termina.
	React.useEffect(() => {
		const unsub = registerCompletionListener(async () => {
			try {
				const [sum, hs] = await Promise.all([getRescueSummary(), listHypershared()]);
				setSummary(sum);
				setHypershared(hs);
			} catch {
				/* silencioso */
			}
		});
		return unsub;
	}, [registerCompletionListener]);

	// Lazy-load de programas por tipo. Se dispara cuando el select cambia a
	// bachillerato/técnica/curso. En modo prioritario no consulta esta lista
	// (usa hypershared ya cargado).
	React.useEffect(() => {
		if (category === 'hipercompartido') {
			setPrograms([]);
			return;
		}
		setProgramsLoading(true);
		listRescuePrograms({ type: category })
			.then((res) => {
				setPrograms(res.programs || []);
				setProgramsLoading(false);
			})
			.catch((err) => {
				toast({
					title: 'Error al cargar programas',
					description: err.message,
					status: 'error',
				});
				setProgramsLoading(false);
			});
	}, [category, toast]);

	const consultarPrograma = async (key) => {
		setRowStatus((s) => ({ ...s, [key]: 'loading' }));
		try {
			const data = await getProgramStatus(key);
			setRowData((d) => ({ ...d, [key]: data }));
			setRowStatus((s) => ({ ...s, [key]: 'ok' }));
		} catch (err) {
			setRowError((e) => ({ ...e, [key]: err.message }));
			setRowStatus((s) => ({ ...s, [key]: 'error' }));
		}
	};

	const abrirModalRestaurar = (program) => {
		setModalProgram(program);
		modal.onOpen();
	};

	const encolarBatch = async ({ key, size, voicePreset }) => {
		try {
			const res = await queueBatchForProgram({ key, size, voicePreset });
			setRowBatch((b) => ({ ...b, [key]: res }));
			trackBatch({
				batch_id: res.batch_id,
				encolados: res.encolados,
				kind: 'programa',
				label: `${modalProgram?.programa} · ${modalProgram?.materia}`,
				started_at: new Date().toISOString(),
			});
			toast({
				title: 'Batch encolado',
				description: `${res.encolados} videos en cola · ${res.batch_id}`,
				status: 'success',
				duration: 6000,
			});
			// Refrescar el status del programa para reflejar la nueva cola.
			await consultarPrograma(key);
		} catch (err) {
			const body = err.response && err.response.data ? err.response.data : {};
			toast({
				title: 'No se pudo encolar',
				description: body.message || err.message,
				status: 'error',
				duration: 8000,
			});
			throw err;
		}
	};

	const filtered = React.useMemo(() => {
		let out = programs;
		if (search.trim()) {
			const q = search.trim().toLowerCase();
			out = out.filter(
				(p) =>
					(p.programa || '').toLowerCase().includes(q) ||
					(p.materia || '').toLowerCase().includes(q),
			);
		}
		if (sortBy === 'estudiantes_desc') {
			out = [...out].sort(
				(a, b) => (b.estudiantes_activos || 0) - (a.estudiantes_activos || 0),
			);
		} else if (sortBy === 'materials_desc') {
			out = [...out].sort(
				(a, b) => (b.total_materials || 0) - (a.total_materials || 0),
			);
		} else if (sortBy === 'programa_asc') {
			out = [...out].sort((a, b) =>
				(a.programa || '').localeCompare(b.programa || ''),
			);
		}
		return out;
	}, [programs, search, sortBy]);

	const isPriorityMode = category === 'hipercompartido';

	return (
		<>
			<KpiSummary data={summary} loading={summaryLoading} />

			<Flex gap="12px" mt="24px" mb="12px" wrap="wrap">
				<Select
					value={category}
					onChange={(e) => setCategory(e.target.value)}
					maxW="260px"
					bg="white"
				>
					{CATEGORY_OPTIONS.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</Select>
				{!isPriorityMode && (
					<>
						<Input
							placeholder="Buscar por programa o materia..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							maxW="300px"
							bg="white"
						/>
						<Select
							value={sortBy}
							onChange={(e) => setSortBy(e.target.value)}
							maxW="240px"
							bg="white"
						>
							<option value="estudiantes_desc">Ordenar por estudiantes ↓</option>
							<option value="materials_desc">Ordenar por videos rotos ↓</option>
							<option value="programa_asc">Ordenar por programa A-Z</option>
						</Select>
						<Box ml="auto" alignSelf="center">
							<Text fontSize="13px" color="gray.600">
								Mostrando {filtered.length} de {programs.length} programas
							</Text>
						</Box>
					</>
				)}
			</Flex>

			{isPriorityMode ? (
				<PriorityView
					hypershared={hypershared}
					activeBatches={activeBatches}
					onEncolarBatch={hypersharedModal.onOpen}
				/>
			) : (
				<TableContainer
					bg="white"
					border="1px"
					borderColor="gray.200"
					borderRadius="8px"
				>
					<Table variant="simple" size="sm">
						<Thead bg="gray.50">
							<Tr>
								<Th>Programa</Th>
								<Th>Materia</Th>
								<Th>Tipo</Th>
								<Th isNumeric>Videos rotos</Th>
								<Th isNumeric>Estudiantes</Th>
								<Th>Progreso</Th>
								<Th>Acción</Th>
							</Tr>
						</Thead>
						<Tbody>
							{programsLoading && (
								<Tr>
									<Td colSpan={7}>
										<Skeleton height="24px" />
									</Td>
								</Tr>
							)}
							{!programsLoading && filtered.length === 0 && (
								<Tr>
									<Td colSpan={7}>
										<Text color="gray.500" textAlign="center" py="24px">
											No hay programas que coincidan con los filtros.
										</Text>
									</Td>
								</Tr>
							)}
							{filtered.map((p) => {
								const rowBatchId = rowBatch[p.key]?.batch_id;
								const isRunning = Boolean(
									rowBatchId && activeBatches.some((b) => b.batch_id === rowBatchId),
								);
								return (
									<ProgramRow
										key={p.key}
										program={p}
										status={rowStatus[p.key] || 'idle'}
										data={rowData[p.key]}
										error={rowError[p.key]}
										size={rowSize[p.key] || 5}
										batch={rowBatch[p.key]}
										isRunning={isRunning}
										onSizeChange={(v) => setRowSize((s) => ({ ...s, [p.key]: v }))}
										onConsultar={() => consultarPrograma(p.key)}
										onRestaurar={() => abrirModalRestaurar(p)}
									/>
								);
							})}
						</Tbody>
					</Table>
				</TableContainer>
			)}

			<RestaurarModal
				isOpen={modal.isOpen}
				onClose={modal.onClose}
				program={modalProgram}
				statusData={modalProgram ? rowData[modalProgram.key] : null}
				defaultSize={modalProgram ? rowSize[modalProgram.key] || 5 : 5}
				onConfirm={encolarBatch}
			/>

			<HypersharedModal
				isOpen={hypersharedModal.isOpen}
				onClose={hypersharedModal.onClose}
				hypershared={hypershared}
				onConfirmed={async (batchResult) => {
					if (batchResult && batchResult.batch_id) {
						trackBatch({
							batch_id: batchResult.batch_id,
							encolados: batchResult.encolados,
							kind: 'prioritario',
							label: `Batch prioritario · ${batchResult.encolados} reutilizables`,
							started_at: new Date().toISOString(),
							// video_ids incluidos en el batch — usados por PriorityView
							// para pintar spinner en las filas de esos videos.
							video_ids: (batchResult.videos_encolados || []).map((v) => v.video_id),
						});
					}
					await refreshHypershared();
					try {
						const sum = await getRescueSummary();
						setSummary(sum);
					} catch {
						/* silencioso */
					}
				}}
				toast={toast}
			/>
		</>
	);
}

// ----------------------------------------------------------------------------
// Sub-componentes del Tab Recuperar
// ----------------------------------------------------------------------------

function KpiSummary({ data, loading }) {
	if (loading) {
		return (
			<Grid templateColumns="repeat(4, 1fr)" gap="12px" mb="20px">
				{[0, 1, 2, 3].map((i) => (
					<GridItem key={i}>
						<Skeleton height="76px" borderRadius="8px" />
					</GridItem>
				))}
			</Grid>
		);
	}
	if (!data) return null;

	const pct = data.restored_percent || 0;
	const faltantes = data.total_materials - data.restored_count - (data.missing_count || 0);
	return (
		<Box mb="20px">
			<Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap="12px">
				<KpiCard
					label="Restaurados"
					value={data.restored_count.toLocaleString()}
					sub={`${pct}% de ${data.total_materials.toLocaleString()}`}
				/>
				<KpiCard
					label="Faltantes"
					value={faltantes.toLocaleString()}
					sub={`por procesar${
						data.missing_count > 0
							? ` · ${data.missing_count.toLocaleString()} sin material en BD`
							: ''
					}`}
				/>
				<KpiCard
					label="Costo estimado"
					value={currency(data.cost_estimated_usd)}
					sub={`${currency(data.cost_per_video_usd)} por video`}
				/>
			</Grid>
			<Progress
				mt="12px"
				value={pct}
				colorScheme="green"
				bg="gray.200"
				borderRadius="6px"
				size="sm"
			/>
		</Box>
	);
}

// ----------------------------------------------------------------------------
// LiveBatchStatusPanel — panel expandible con estado live del batch encolado.
// Polling cada 10s al endpoint proxy /api/rescue/batches/:id/live-status. Al
// expandir muestra badges por estado, progress bar y tabla con cada material,
// estado y % de progreso (mismo detalle que la vista vieja de rescate-videos).
// Cuando todos los jobs están terminal (completed/failed), llama onCompleted.
// ----------------------------------------------------------------------------

const TERMINAL_STATES = new Set(['completed', 'failed']);
const STATE_LABEL_LIVE = {
	queued: 'En cola',
	generating_script: 'Guion',
	generating_audio: 'Audio',
	rendering: 'Render',
	uploading: 'Subiendo',
	completed: 'Completado',
	failed: 'Falló',
};
const STATE_COLOR_LIVE = {
	queued: 'gray',
	generating_script: 'purple',
	generating_audio: 'purple',
	rendering: 'purple',
	uploading: 'purple',
	completed: 'green',
	failed: 'red',
};

function LiveBatchStatusPanel({ batch, onDismiss, onCompleted }) {
	const [status, setStatus] = React.useState(null);
	const [notFound, setNotFound] = React.useState(false);
	const [expanded, setExpanded] = React.useState(true);
	const completedRef = React.useRef(false);

	const fetchStatus = React.useCallback(async () => {
		try {
			const data = await getBatchLiveStatus(batch.batch_id);
			if (data.error === 'not_found_in_worker') {
				setNotFound(true);
				return null;
			}
			setStatus(data);
			return data;
		} catch {
			return null;
		}
	}, [batch.batch_id]);

	React.useEffect(() => {
		let cancelled = false;
		const tick = async () => {
			const data = await fetchStatus();
			if (cancelled || !data) return;
			const jobs = data.jobs || [];
			const allTerminal = jobs.length > 0 && jobs.every((j) => TERMINAL_STATES.has(j.state));
			if (allTerminal && !completedRef.current) {
				completedRef.current = true;
				try {
					await onCompleted();
				} catch {
					/* silencioso */
				}
			}
		};
		tick();
		const interval = setInterval(tick, 10000);
		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [fetchStatus, onCompleted]);

	if (notFound) {
		return (
			<Flex
				bg="gray.100"
				border="1px"
				borderColor="gray.300"
				borderRadius="8px"
				p="10px 14px"
				align="center"
				gap="12px"
				fontSize="12px"
			>
				<Badge colorScheme="gray">Batch no encontrado</Badge>
				<Text color="gray.700">{batch.batch_id}</Text>
				<Button size="xs" variant="ghost" onClick={onDismiss} ml="auto">
					Cerrar
				</Button>
			</Flex>
		);
	}

	const jobs = (status && status.jobs) || [];
	const summary =
		(status && status.summary) ||
		jobs.reduce((acc, j) => {
			acc[j.state] = (acc[j.state] || 0) + 1;
			return acc;
		}, {});
	const total = (status && status.total) || jobs.length || batch.encolados || 0;
	const completed = summary.completed || 0;
	const failed = summary.failed || 0;
	const inProgress = total - completed - failed;
	const allDone = total > 0 && inProgress === 0;
	const pct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;

	return (
		<Box
			bg={allDone ? '#F0FDF4' : '#FAFAFF'}
			border="1px"
			borderColor={allDone ? '#22C55E' : '#6C4EE0'}
			borderRadius="8px"
			overflow="hidden"
		>
			<Flex
				p="12px 16px"
				align="center"
				gap="12px"
				fontSize="12px"
				wrap="wrap"
				cursor="pointer"
				onClick={() => setExpanded((v) => !v)}
				_hover={{ bg: allDone ? '#E7FBEE' : '#F0EEFF' }}
			>
				<Text fontSize="11px" color="gray.500">
					{expanded ? '▾' : '▸'}
				</Text>
				<Badge colorScheme={batch.kind === 'prioritario' ? 'yellow' : 'purple'}>
					{batch.kind === 'prioritario' ? 'Prioritario' : 'Programa'}
				</Badge>
				<Text fontWeight="600" color="gray.800">
					{batch.label || batch.batch_id}
				</Text>
				<Text fontSize="11px" color="gray.500" fontFamily="mono">
					{batch.batch_id}
				</Text>
				<Flex gap="6px" ml="auto" align="center">
					{allDone ? (
						<Badge colorScheme="green">Terminado</Badge>
					) : (
						<Text fontSize="12px" color="purple.700" fontWeight="600">
							{completed + failed}/{total} · {pct}%
						</Text>
					)}
					<Button
						size="xs"
						variant="ghost"
						onClick={(e) => {
							e.stopPropagation();
							onDismiss();
						}}
					>
						{allDone ? 'Cerrar' : 'Ocultar'}
					</Button>
				</Flex>
			</Flex>

			{expanded && total > 0 && (
				<Box p="0 16px 14px">
					<Progress
						value={pct}
						colorScheme={allDone ? 'green' : 'purple'}
						size="sm"
						borderRadius="4px"
						mb="10px"
						bg="gray.100"
					/>

					<Flex gap="6px" wrap="wrap" mb="12px">
						{Object.entries(summary).map(([state, count]) => (
							<Badge
								key={state}
								colorScheme={STATE_COLOR_LIVE[state] || 'gray'}
								fontSize="10px"
							>
								{STATE_LABEL_LIVE[state] || state}: {count}
							</Badge>
						))}
					</Flex>

					<TableContainer maxHeight="320px" overflowY="auto" bg="white" borderRadius="6px">
						<Table size="sm">
							<Thead bg="gray.50" position="sticky" top="0" zIndex={1}>
								<Tr>
									<Th>Material</Th>
									<Th>Tema</Th>
									<Th>Estado</Th>
									<Th isNumeric>Progreso</Th>
								</Tr>
							</Thead>
							<Tbody>
								{jobs.map((job) => (
									<Tr key={job.job_id || job.material_id}>
										<Td fontSize="12px" fontWeight="600">
											#{job.material_id}
										</Td>
										<Td fontSize="12px" color="gray.700">
											{job.topic ? (
												job.topic.length > 60 ? (
													`${job.topic.slice(0, 60)}…`
												) : (
													job.topic
												)
											) : (
												<Text color="gray.400" fontSize="11px">
													—
												</Text>
											)}
										</Td>
										<Td>
											<Badge
												colorScheme={STATE_COLOR_LIVE[job.state] || 'gray'}
												fontSize="10px"
											>
												{STATE_LABEL_LIVE[job.state] || job.state}
											</Badge>
										</Td>
										<Td isNumeric fontSize="12px" color="gray.700">
											{typeof job.progress === 'number' ? `${job.progress}%` : '—'}
										</Td>
									</Tr>
								))}
							</Tbody>
						</Table>
					</TableContainer>
				</Box>
			)}
		</Box>
	);
}

function KpiCard({ label, value, sub }) {
	return (
		<Box bg="white" border="1px" borderColor="gray.200" borderRadius="8px" p="14px">
			<Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase">
				{label}
			</Text>
			<Text fontSize="22px" fontWeight="700" color="gray.800" mt="2px">
				{value}
			</Text>
			<Text fontSize="12px" color="gray.500">
				{sub}
			</Text>
		</Box>
	);
}

function PriorityView({ hypershared, activeBatches = [], onEncolarBatch }) {
	if (!hypershared) {
		return <Skeleton height="360px" borderRadius="8px" />;
	}

	const sortedVideos = [...(hypershared.videos || [])].sort((a, b) => {
		// primero pendientes (mayor impacto), luego restaurados
		if (a.restored !== b.restored) return a.restored ? 1 : -1;
		return (b.estudiantes_afectados || 0) - (a.estudiantes_afectados || 0);
	});

	// Set de video_ids incluidos en algún batch activo — pinta spinner en la fila.
	const inFlightVideoIds = new Set();
	for (const b of activeBatches) {
		for (const vid of b.video_ids || []) inFlightVideoIds.add(vid);
	}

	return (
		<>
			<Flex justify="space-between" align="center" mb="12px" wrap="wrap" gap="12px">
				<Text fontSize="13px" color="gray.600">
					{hypershared.pending_count} pendientes · {hypershared.restored_count} restaurados
					· {hypershared.total} totales
				</Text>
				<Button
					colorScheme="yellow"
					size="sm"
					onClick={onEncolarBatch}
					isDisabled={hypershared.pending_count === 0}
				>
					Encolar batch prioritario
				</Button>
			</Flex>

			<TableContainer bg="white" border="1px" borderColor="gray.200" borderRadius="8px">
				<Table variant="simple" size="sm">
					<Thead bg="gray.50">
						<Tr>
							<Th>Video</Th>
							<Th isNumeric>Lecciones que reconecta</Th>
							<Th isNumeric>Programas afectados</Th>
							<Th isNumeric>Estudiantes activos</Th>
							<Th>Estado</Th>
						</Tr>
					</Thead>
					<Tbody>
						{sortedVideos.map((v) => {
							const inFlight = inFlightVideoIds.has(v.video_id);
							return (
							<Tr key={v.video_id} bg={inFlight ? 'orange.50' : undefined}>
								<Td>
									<Flex align="center" gap="8px">
										{inFlight && (
											<Tooltip label="En cola en un batch prioritario activo" hasArrow>
												<Spinner size="xs" color="orange.500" thickness="2px" />
											</Tooltip>
										)}
										<Text fontWeight="600" fontSize="13px" color="gray.800">
											#{v.video_id} · {v.nombre}
										</Text>
									</Flex>
								</Td>
								<Td isNumeric>
									<Text fontSize="13px" color="gray.700">
										{v.reuso_materials}
									</Text>
								</Td>
								<Td isNumeric>
									<Text fontSize="13px" color="gray.700">
										{v.programas_afectados}
									</Text>
								</Td>
								<Td isNumeric>
									<Text fontSize="13px" color="gray.700">
										{v.estudiantes_afectados.toLocaleString()}
									</Text>
								</Td>
								<Td>
									{inFlight ? (
										<Badge colorScheme="orange">En cola</Badge>
									) : v.restored ? (
										<Badge colorScheme="green">Restaurado</Badge>
									) : (
										<Badge colorScheme="yellow">Pendiente</Badge>
									)}
								</Td>
							</Tr>
							);
						})}
					</Tbody>
				</Table>
			</TableContainer>
		</>
	);
}

// ----------------------------------------------------------------------------
// HypersharedModal — encolar batch prioritario de reutilizables al pipeline IA
// ----------------------------------------------------------------------------

function HypersharedModal({ isOpen, onClose, hypershared, onConfirmed, toast }) {
	const [size, setSize] = React.useState(5);
	const [voicePreset, setVoicePreset] = React.useState('bilingue');
	const [submitting, setSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (isOpen) {
			setSize(5);
			setVoicePreset('bilingue');
			setSubmitting(false);
		}
	}, [isOpen]);

	const pendingVideos = React.useMemo(() => {
		if (!hypershared) return [];
		return (hypershared.videos || [])
			.filter((v) => !v.restored)
			.sort((a, b) => (b.estudiantes_afectados || 0) - (a.estudiantes_afectados || 0));
	}, [hypershared]);

	const toQueuePreview = pendingVideos.slice(0, size);
	const impactoLecciones = toQueuePreview.reduce((acc, v) => acc + (v.reuso_materials || 0), 0);
	const costoEstimado = (toQueuePreview.length * COST_PER_VIDEO_USD).toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
	const tiempoEstimado = Math.round(toQueuePreview.length * 7);

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			const res = await queueHypersharedBatch({ size, voicePreset });
			toast({
				title: 'Batch prioritario encolado',
				description: `${res.encolados} videos · reconectará ~${res.expected_impact.lecciones_reconectadas} lecciones · ${res.batch_id}`,
				status: 'success',
				duration: 8000,
			});
			await onConfirmed(res);
			onClose();
		} catch (err) {
			const body = err.response && err.response.data ? err.response.data : {};
			toast({
				title: 'No se pudo encolar',
				description: body.message || err.message,
				status: 'error',
				duration: 8000,
			});
			setSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Encolar batch prioritario de reutilizables</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Text fontSize="13px" color="gray.600" mb="10px">
						Los videos hipercompartidos son alto-impacto: cada uno cubre 30–80 lecciones
						distribuidas en múltiples programas. Se generan con el pipeline IA como cualquier
						otro batch, pero los procesamos primero para maximizar lecciones restauradas por
						USD gastado. La voz por defecto es bilingüe porque son mayoritariamente Inglés.
					</Text>

					<Grid templateColumns="1fr 1fr" gap="14px" mt="4px">
						<FormControl>
							<FormLabel fontSize="12px" color="gray.600" mb="4px">
								Cantidad a encolar
							</FormLabel>
							<Select
								size="sm"
								value={size}
								onChange={(e) => setSize(Number(e.target.value))}
								bg="white"
							>
								{[1, 2, 3, 5, 10].map((n) => (
									<option key={n} value={n}>
										{n} {n === 1 ? 'video' : 'videos'}
									</option>
								))}
							</Select>
						</FormControl>
						<FormControl>
							<FormLabel fontSize="12px" color="gray.600" mb="4px">
								Voz del batch
							</FormLabel>
							<Select
								size="sm"
								value={voicePreset}
								onChange={(e) => setVoicePreset(e.target.value)}
								bg="white"
							>
								{VOICE_PRESET_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Box
						mt="14px"
						bg="#FEF3C7"
						border="1px"
						borderColor="#FCD34D"
						borderRadius="6px"
						p="10px 12px"
					>
						<Text fontSize="12px" fontWeight="600" color="#78350F">
							Impacto esperado del batch
						</Text>
						<Flex mt="6px" gap="16px" fontSize="12px" color="#92400E" wrap="wrap">
							<Box>
								Videos: <b>{toQueuePreview.length}</b>
							</Box>
							<Box>
								Lecciones reconectadas: <b>~{impactoLecciones}</b>
							</Box>
							<Box>
								Costo: <b>{costoEstimado}</b>
							</Box>
							<Box>
								Tiempo: <b>~{tiempoEstimado} min</b>
							</Box>
						</Flex>
					</Box>

					<Text fontSize="12px" fontWeight="600" color="gray.600" mt="14px" mb="6px">
						Videos que se van a encolar (ordenados por estudiantes activos)
					</Text>
					<Box
						maxH="180px"
						overflowY="auto"
						border="1px"
						borderColor="gray.200"
						borderRadius="6px"
						bg="gray.50"
					>
						{toQueuePreview.length === 0 && (
							<Text fontSize="12px" color="gray.500" p="12px">
								No hay videos pendientes.
							</Text>
						)}
						{toQueuePreview.map((v) => (
							<Flex
								key={v.video_id}
								px="10px"
								py="6px"
								borderBottom="1px"
								borderColor="gray.100"
								fontSize="12px"
								justify="space-between"
								align="center"
							>
								<Box>
									<Text fontWeight="600" color="gray.800">
										#{v.video_id} · {v.nombre}
									</Text>
									<Text fontSize="11px" color="gray.500">
										{v.reuso_materials} lecciones · {v.programas_afectados} programas
									</Text>
								</Box>
								<Text fontSize="11px" color="gray.600" whiteSpace="nowrap">
									{v.estudiantes_afectados.toLocaleString()} estudiantes
								</Text>
							</Flex>
						))}
					</Box>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
						Cancelar
					</Button>
					<Button
						colorScheme="yellow"
						onClick={handleConfirm}
						isDisabled={toQueuePreview.length === 0}
						isLoading={submitting}
					>
						Confirmar y encolar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}


function ProgramRow({
	program,
	status,
	data,
	error,
	size,
	batch,
	isRunning,
	onSizeChange,
	onConsultar,
	onRestaurar,
}) {
	const isLoading = status === 'loading';
	const isOk = status === 'ok' && data;
	const isError = status === 'error';
	const restaurarEnabled = isOk && data.pending > 0;

	return (
		<Tr bg={isRunning ? 'orange.50' : undefined}>
			<Td>
				<Flex align="center" gap="8px">
					{isRunning && (
						<Tooltip label="Batch de este programa en curso" hasArrow>
							<Spinner size="xs" color="orange.500" thickness="2px" />
						</Tooltip>
					)}
					<Box>
						<Text fontWeight="600" fontSize="13px" color="gray.800">
							{program.programa}
						</Text>
						{program.grado && program.grado !== program.programa && (
							<Text fontSize="11px" color="gray.500">
								{program.grado}
							</Text>
						)}
					</Box>
				</Flex>
			</Td>
			<Td>
				<Text fontSize="13px" color="gray.700">
					{program.materia}
				</Text>
			</Td>
			<Td>
				<Badge colorScheme={PROGRAM_TYPE_COLOR[program.program_type] || 'gray'}>
					{PROGRAM_TYPE_LABEL[program.program_type] || program.program_type}
				</Badge>
			</Td>
			<Td isNumeric>
				<Text fontSize="13px" color="gray.700">
					{isOk ? (
						<>
							<b>{data.pending}</b> / {data.total_materials}
						</>
					) : (
						program.total_materials
					)}
				</Text>
			</Td>
			<Td isNumeric>
				<Text fontSize="13px" color="gray.700">
					{(program.estudiantes_activos || 0).toLocaleString()}
				</Text>
			</Td>
			<Td>
				{isLoading && <Skeleton height="14px" width="120px" />}
				{isOk && (
					<Box minW="140px">
						<Progress
							value={
								data.total_materials > 0
									? (data.restored / data.total_materials) * 100
									: 0
							}
							size="xs"
							colorScheme="green"
							bg="gray.100"
							borderRadius="4px"
						/>
						<Text fontSize="11px" color="gray.500" mt="3px">
							{data.restored} restaurados · {data.hypershared} vía humano
						</Text>
					</Box>
				)}
				{batch && batch.batch_id && (
					<Badge mt="4px" colorScheme="purple" fontSize="10px">
						Encolado: {batch.encolados} · {batch.batch_id.slice(-6)}
					</Badge>
				)}
				{isError && (
					<Text fontSize="11px" color="red.500" title={error}>
						Error al consultar
					</Text>
				)}
				{status === 'idle' && (
					<Text fontSize="11px" color="gray.400">
						Sin consultar
					</Text>
				)}
			</Td>
			<Td>
				<Flex gap="6px" align="center">
					<Button
						size="xs"
						colorScheme="purple"
						variant={isOk ? 'outline' : 'solid'}
						onClick={onConsultar}
						isLoading={isLoading}
					>
						{isOk ? 'Refrescar' : 'Consultar'}
					</Button>
					{isOk && (
						<>
							<Select
								size="xs"
								width="70px"
								value={size}
								onChange={(e) => onSizeChange(Number(e.target.value))}
								bg="white"
								title="Cuántos videos encolar en este batch"
							>
								{[1, 2, 3, 5, 10].map((n) => (
									<option key={n} value={n}>
										{n}
									</option>
								))}
							</Select>
							<Button
								size="xs"
								colorScheme="green"
								onClick={onRestaurar}
								isDisabled={!restaurarEnabled}
								title={
									restaurarEnabled
										? 'Encolar batch al pipeline IA'
										: 'No hay pendientes por IA en este programa'
								}
							>
								Restaurar
							</Button>
						</>
					)}
				</Flex>
			</Td>
		</Tr>
	);
}

// ----------------------------------------------------------------------------
// RestaurarModal — confirmación antes de encolar batch al worker
// ----------------------------------------------------------------------------

function RestaurarModal({ isOpen, onClose, program, statusData, defaultSize, onConfirm }) {
	const [size, setSize] = React.useState(defaultSize);
	const [voicePreset, setVoicePreset] = React.useState('');
	const [submitting, setSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (isOpen) {
			setSize(defaultSize);
			setVoicePreset('');
			setSubmitting(false);
		}
	}, [isOpen, defaultSize]);

	if (!program) return null;

	const pendientesReales = statusData ? statusData.pending : program.total_materials;
	const encolarReal = Math.min(size, pendientesReales);
	const costoEstimado = (encolarReal * COST_PER_VIDEO_USD).toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
	const tiempoEstimado = Math.round(encolarReal * 7); // ~7 min por video

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			await onConfirm({ key: program.key, size, voicePreset });
			onClose();
		} catch {
			setSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Confirmar restauración</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Text fontSize="13px" color="gray.600" mb="4px">
						Programa
					</Text>
					<Text fontSize="15px" fontWeight="600" color="gray.800">
						{program.programa} · {program.materia}
					</Text>
					<Text fontSize="12px" color="gray.500" mt="4px">
						{PROGRAM_TYPE_LABEL[program.program_type] || program.program_type}
						{program.grado && ` · ${program.grado}`}
					</Text>

					<Divider my="14px" />

					<Grid templateColumns="1fr 1fr" gap="14px">
						<FormControl>
							<FormLabel fontSize="12px" color="gray.600" mb="4px">
								Cantidad a encolar
							</FormLabel>
							<Select
								size="sm"
								value={size}
								onChange={(e) => setSize(Number(e.target.value))}
								bg="white"
							>
								{[1, 2, 3, 5, 10].map((n) => (
									<option key={n} value={n}>
										{n} {n === 1 ? 'video' : 'videos'}
									</option>
								))}
							</Select>
						</FormControl>
						<FormControl>
							<FormLabel fontSize="12px" color="gray.600" mb="4px">
								Voz del batch
							</FormLabel>
							<Select
								size="sm"
								value={voicePreset}
								onChange={(e) => setVoicePreset(e.target.value)}
								bg="white"
							>
								{VOICE_PRESET_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</FormControl>
					</Grid>

					<Box mt="14px" bg="gray.50" borderRadius="6px" p="10px 12px">
						<Text fontSize="12px" color="gray.600">
							Se van a encolar <b>{encolarReal}</b> videos (los pendientes se procesan primero;
							se excluyen los ya restaurados y los reutilizables).
						</Text>
						<Flex mt="8px" gap="16px" fontSize="12px" color="gray.700">
							<Box>
								Costo estimado: <b>{costoEstimado}</b>
							</Box>
							<Box>
								Tiempo aprox: <b>~{tiempoEstimado} min</b>
							</Box>
						</Flex>
					</Box>

					{statusData && (
						<Box mt="10px" fontSize="11px" color="gray.500">
							Contexto del programa: {statusData.pending} pendientes ·{' '}
							{statusData.restored} ya restaurados · {statusData.hypershared} vía
							grabación humana ·{' '}
							{statusData.estudiantes_activos.toLocaleString()} estudiantes activos.
						</Box>
					)}
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
						Cancelar
					</Button>
					<Button colorScheme="green" onClick={handleConfirm} isLoading={submitting}>
						Confirmar y encolar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

// ============================================================================
// Tab 2 — Historial
// ============================================================================

const STAGE_LABEL = {
	script: 'Guion',
	sanitize: 'Sanitización',
	audio: 'Audio',
	tts: 'TTS',
	rendering: 'Render',
	uploading: 'Upload',
	callback: 'Callback',
};

function classifyError({ retriable, stage }) {
	if (retriable === true) return { key: 'transient', label: 'Transitorio', color: 'orange' };
	if (retriable === false && (stage === 'script' || stage === 'sanitize')) {
		return { key: 'input', label: 'Determinístico (input)', color: 'red' };
	}
	if (retriable === false) return { key: 'system', label: 'Determinístico (sistema)', color: 'purple' };
	return { key: 'unknown', label: 'Sin clasificar', color: 'gray' };
}

const timeAgo = (iso) => {
	if (!iso) return '';
	const diffMs = Date.now() - new Date(iso).getTime();
	const mins = Math.floor(diffMs / 60000);
	if (mins < 1) return 'hace segundos';
	if (mins < 60) return `hace ${mins} min`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `hace ${hrs} h`;
	const days = Math.floor(hrs / 24);
	return `hace ${days} d`;
};

// Los batches de reintento se nombran <parentId>-retry-<hash6>. Extraer el
// parentId permite mostrar cuántos reintentos hijos tiene un batch en el
// listado y evitar disparar reintentos duplicados por click accidental.
const RETRY_SUFFIX_RE = /-retry-[a-z0-9]+$/i;
const getRetryParentId = (batchId) => {
	const m = batchId && batchId.match(RETRY_SUFFIX_RE);
	return m ? batchId.slice(0, -m[0].length) : null;
};

function HistorialTab() {
	const { trackBatch, registerCompletionListener } = useRescueBatches();
	const [batches, setBatches] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [progress, setProgress] = React.useState(null);
	const [detailBatch, setDetailBatch] = React.useState(null);
	const [items, setItems] = React.useState({});
	const [itemsLoading, setItemsLoading] = React.useState({});
	const [retryBatch, setRetryBatch] = React.useState(null);
	const detailModal = useDisclosure();
	const [typeFilter, setTypeFilter] = React.useState('all');
	const [resultFilter, setResultFilter] = React.useState('all');
	const retryModal = useDisclosure();
	const toast = useToast();

	// UX del reintento: `pendingRetries` bloquea el botón del padre mientras el
	// request está en vuelo (evita el doble-click clásico cuando el refresh
	// tarda 1-3 s). `newBatchIds` guarda los ids que aparecieron en el último
	// refresh para pintar highlight amarillo suave durante 4 s.
	const [pendingRetries, setPendingRetries] = React.useState(() => new Set());
	const [newBatchIds, setNewBatchIds] = React.useState(() => new Set());
	const prevBatchIdsRef = React.useRef(null);

	const cargarHistorial = React.useCallback(async () => {
		setLoading(true);
		try {
			const [historial, prog] = await Promise.all([
				listRescueHistory({ limit: 50 }),
				getDailyProgress({ days: 30 }),
			]);
			setBatches(historial.batches || []);
			setProgress(prog);
		} catch (err) {
			toast({
				title: 'Error al cargar historial',
				description: err.message,
				status: 'error',
			});
		}
		setLoading(false);
	}, [toast]);

	React.useEffect(() => {
		cargarHistorial();
	}, [cargarHistorial]);

	// Cuando un batch (de cualquier tab) termina, recargar historial + chart.
	React.useEffect(() => {
		const unsub = registerCompletionListener(() => cargarHistorial());
		return unsub;
	}, [registerCompletionListener, cargarHistorial]);

	const filteredBatches = React.useMemo(() => {
		let out = batches;
		if (typeFilter !== 'all') {
			out = out.filter((b) => b.type === typeFilter);
		}
		if (resultFilter === 'with_fails') {
			out = out.filter((b) => b.failed > 0);
		} else if (resultFilter === 'clean') {
			out = out.filter((b) => b.failed === 0);
		}
		return out;
	}, [batches, typeFilter, resultFilter]);

	// Mapa parentId → [retryChildId, ...] para pintar el contador de reintentos
	// junto al botón del padre. Se recomputa solo al cambiar la lista de batches.
	const retryChildrenByParent = React.useMemo(() => {
		const map = new Map();
		for (const b of batches) {
			const parentId = getRetryParentId(b.batch_id);
			if (!parentId) continue;
			if (!map.has(parentId)) map.set(parentId, []);
			map.get(parentId).push(b.batch_id);
		}
		return map;
	}, [batches]);

	// Detectar batch_ids nuevos entre refreshes para el highlight amarillo.
	// La primera vez (ref null) solo se snapshotea; a partir del segundo
	// refresh, cualquier id que no estaba antes queda "nuevo" por 4 s.
	React.useEffect(() => {
		const currentIds = new Set(batches.map((b) => b.batch_id));
		const prev = prevBatchIdsRef.current;
		prevBatchIdsRef.current = currentIds;
		if (!prev) return;
		const newlyAdded = new Set();
		for (const id of currentIds) {
			if (!prev.has(id)) newlyAdded.add(id);
		}
		if (newlyAdded.size === 0) return;
		setNewBatchIds(newlyAdded);
		const t = setTimeout(() => setNewBatchIds(new Set()), 4000);
		return () => clearTimeout(t);
	}, [batches]);

	const openDetail = async (batch) => {
		setDetailBatch(batch);
		detailModal.onOpen();
		if (items[batch.batch_id]) return; // ya cargado
		setItemsLoading((s) => ({ ...s, [batch.batch_id]: true }));
		try {
			const data = await getBatchItems(batch.batch_id);
			setItems((s) => ({ ...s, [batch.batch_id]: data }));
		} catch (err) {
			toast({
				title: 'Error al cargar items',
				description: err.message,
				status: 'error',
			});
		}
		setItemsLoading((s) => ({ ...s, [batch.batch_id]: false }));
	};

	const abrirModalReintento = (batch) => {
		setRetryBatch(batch);
		retryModal.onOpen();
	};

	const confirmarReintento = async (voicePreset) => {
		const parentBatchId = retryBatch.batch_id;
		// Marcar como pending ANTES del await para que la fila del padre
		// muestre "Encolando…" inmediatamente y bloquee un segundo click.
		setPendingRetries((prev) => {
			const next = new Set(prev);
			next.add(parentBatchId);
			return next;
		});
		try {
			const res = await retryFailedBatch(parentBatchId, { voicePreset });
			toast({
				title: 'Reintento encolado',
				description: `${res.encolados} fallidos re-encolados como ${res.retry_batch_id}`,
				status: 'success',
				duration: 8000,
			});
			retryModal.onClose();
			await cargarHistorial();
		} catch (err) {
			const body = err.response && err.response.data ? err.response.data : {};
			toast({
				title: 'No se pudo reintentar',
				description: body.message || err.message,
				status: 'error',
				duration: 8000,
			});
			throw err;
		} finally {
			setPendingRetries((prev) => {
				const next = new Set(prev);
				next.delete(parentBatchId);
				return next;
			});
		}
	};

	return (
		<>
			<DailyProgressChart data={progress} loading={loading} />

			<Flex justify="space-between" align="center" mb="12px" wrap="wrap" gap="12px">
				<Flex gap="8px" wrap="wrap" align="center">
					<Select
						size="sm"
						maxW="180px"
						bg="white"
						value={typeFilter}
						onChange={(e) => setTypeFilter(e.target.value)}
					>
						<option value="all">Todos los tipos</option>
						<option value="programa">Programa</option>
						<option value="hipercompartidos">Prioritario</option>
					</Select>
					<Select
						size="sm"
						maxW="200px"
						bg="white"
						value={resultFilter}
						onChange={(e) => setResultFilter(e.target.value)}
					>
						<option value="all">Todos los resultados</option>
						<option value="with_fails">Con fallos</option>
						<option value="clean">Sin fallos</option>
					</Select>
					<Text fontSize="12px" color="gray.500">
						{loading
							? 'Cargando...'
							: `${filteredBatches.length} de ${batches.length} batches`}
					</Text>
				</Flex>
				<Button size="xs" variant="outline" onClick={cargarHistorial} isDisabled={loading}>
					Refrescar
				</Button>
			</Flex>

			<TableContainer bg="white" border="1px" borderColor="gray.200" borderRadius="8px">
				<Table variant="simple" size="sm">
					<Thead bg="gray.50">
						<Tr>
							<Th>Batch</Th>
							<Th>Tipo</Th>
							<Th isNumeric>Videos</Th>
							<Th>Resultado</Th>
							<Th>Costo</Th>
							<Th>Último callback</Th>
							<Th>Acción</Th>
						</Tr>
					</Thead>
					<Tbody>
						{loading && (
							<Tr>
								<Td colSpan={7}>
									<Skeleton height="24px" />
								</Td>
							</Tr>
						)}
						{!loading && filteredBatches.length === 0 && (
							<Tr>
								<Td colSpan={7}>
									<Text color="gray.500" textAlign="center" py="24px">
										{batches.length === 0
											? 'No hay batches con callbacks aún.'
											: 'Ningún batch coincide con los filtros.'}
									</Text>
								</Td>
							</Tr>
						)}
						{filteredBatches.map((b) => {
							const isNew = newBatchIds.has(b.batch_id);
							const isPending = pendingRetries.has(b.batch_id);
							const childRetries = retryChildrenByParent.get(b.batch_id) || [];
							// Un batch está en curso mientras queden items sin callback:
							// total = completed + failed → terminado.
							const isRunning = b.total > b.completed + b.failed;
							const bg = isRunning ? 'orange.50' : isNew ? 'yellow.100' : undefined;
							return (
								<Tr
									key={b.batch_id}
									cursor="pointer"
									onClick={() => openDetail(b)}
									_hover={{ bg: bg || 'gray.50' }}
									bg={bg}
									transition="background-color 800ms ease-out"
								>
									<Td>
										<Flex align="center" gap="8px">
											{isRunning && (
												<Tooltip
													label={`En curso · ${b.completed + b.failed}/${b.total}`}
													hasArrow
												>
													<Spinner size="xs" color="orange.500" thickness="2px" />
												</Tooltip>
											)}
											<Text fontSize="12px" fontWeight="600" color="gray.800">
												{b.batch_id}
											</Text>
										</Flex>
									</Td>
									<Td>
										<Badge
											colorScheme={b.type === 'hipercompartidos' ? 'yellow' : 'purple'}
										>
											{b.type === 'hipercompartidos' ? 'Prioritario' : 'Programa'}
										</Badge>
									</Td>
									<Td isNumeric>
										<Text fontSize="13px" color="gray.700">
											{b.total}
										</Text>
									</Td>
									<Td>
										<Flex gap="6px" wrap="wrap">
											{b.completed > 0 && (
												<Badge colorScheme="green" fontSize="10px">
													✓ {b.completed}
												</Badge>
											)}
											{b.failed > 0 && (
												<Badge colorScheme="red" fontSize="10px">
													✕ {b.failed}
												</Badge>
											)}
										</Flex>
									</Td>
									<Td>
										<Text fontSize="12px" color="gray.700">
											USD ${b.cost_estimated_usd.toFixed(2)}
										</Text>
									</Td>
									<Td>
										<Text fontSize="12px" color="gray.600" title={b.last_processed_at}>
											{timeAgo(b.last_processed_at)}
										</Text>
									</Td>
									<Td onClick={(e) => e.stopPropagation()}>
										{isPending ? (
											<Flex align="center" gap="6px">
												<Spinner size="xs" color="orange.500" thickness="2px" />
												<Text fontSize="11px" color="orange.700" fontWeight="600">
													Encolando…
												</Text>
											</Flex>
										) : (
											<Flex align="center" gap="6px">
												{b.failed > 0 && (
													<Button
														size="xs"
														colorScheme="orange"
														variant={childRetries.length > 0 ? 'ghost' : 'outline'}
														onClick={(e) => {
															e.stopPropagation();
															abrirModalReintento(b);
														}}
													>
														Reintentar {b.failed}
													</Button>
												)}
												{childRetries.length > 0 && (
													<Tooltip
														hasArrow
														placement="left"
														label={
															<Box>
																<Text fontSize="11px" fontWeight="600" mb="4px">
																	Reintentos ya lanzados
																</Text>
																{childRetries.map((id) => (
																	<Text key={id} fontSize="10px" fontFamily="mono">
																		{id.replace(/^.*-retry-/, '-retry-')}
																	</Text>
																))}
															</Box>
														}
													>
														<Badge colorScheme="blue" fontSize="10px" px="6px" cursor="help">
															↻ {childRetries.length}
														</Badge>
													</Tooltip>
												)}
											</Flex>
										)}
									</Td>
								</Tr>
							);
						})}
					</Tbody>
				</Table>
			</TableContainer>

			<BatchDetailModal
				isOpen={detailModal.isOpen}
				onClose={detailModal.onClose}
				batch={detailBatch}
				items={detailBatch ? items[detailBatch.batch_id] : null}
				loading={detailBatch ? itemsLoading[detailBatch.batch_id] : false}
				onRetry={() => {
					detailModal.onClose();
					abrirModalReintento(detailBatch);
				}}
				onRegenerated={async () => {
					// Refresh del historial para que aparezca el nuevo batch de
					// regeneración con su spinner "en curso". El detail modal
					// queda abierto porque el usuario puede querer regenerar
					// varios items del mismo batch en secuencia.
					await cargarHistorial();
				}}
			/>

			<ReintentarModal
				isOpen={retryModal.isOpen}
				onClose={retryModal.onClose}
				batch={retryBatch}
				onConfirm={confirmarReintento}
			/>
		</>
	);
}

// ----------------------------------------------------------------------------
// DailyProgressChart — SVG puro (sin dependencias) — barras verticales
// verdes (completados) + rojas (fallidos) por día, últimos 30 días.
// ----------------------------------------------------------------------------

function DailyProgressChart({ data, loading }) {
	if (loading || !data) {
		return <Skeleton height="160px" borderRadius="8px" mb="14px" />;
	}
	const { buckets, total_completed, total_failed } = data;
	if (buckets.length === 0) return null;

	const width = 900;
	const height = 160;
	const padding = { top: 20, right: 12, bottom: 24, left: 34 };
	const chartW = width - padding.left - padding.right;
	const chartH = height - padding.top - padding.bottom;
	const barGap = 3;
	const barW = Math.max((chartW - barGap * (buckets.length - 1)) / buckets.length, 4);
	const maxCount = Math.max(1, ...buckets.map((b) => b.completed + b.failed));
	const scale = (v) => (v / maxCount) * chartH;

	return (
		<Box
			bg="white"
			border="1px"
			borderColor="gray.200"
			borderRadius="8px"
			p="14px 18px"
			mb="14px"
		>
			<Flex justify="space-between" align="center" mb="6px">
				<Box>
					<Text fontSize="13px" fontWeight="600" color="gray.800">
						Progreso diario · últimos 30 días
					</Text>
					<Text fontSize="11px" color="gray.500">
						{total_completed} completados · {total_failed} fallidos
					</Text>
				</Box>
				<Flex gap="12px" fontSize="11px" color="gray.600">
					<Flex align="center" gap="4px">
						<Box w="10px" h="10px" borderRadius="2px" bg="green.400" />
						Completados
					</Flex>
					<Flex align="center" gap="4px">
						<Box w="10px" h="10px" borderRadius="2px" bg="red.400" />
						Fallidos
					</Flex>
				</Flex>
			</Flex>

			<Box overflowX="auto">
				<svg width={width} height={height} style={{ display: 'block', minWidth: '100%' }}>
					{/* Eje horizontal */}
					<line
						x1={padding.left}
						y1={padding.top + chartH}
						x2={padding.left + chartW}
						y2={padding.top + chartH}
						stroke="#E5E7EB"
						strokeWidth="1"
					/>
					{/* Ticks del eje Y */}
					{[0, 0.5, 1].map((frac) => {
						const y = padding.top + chartH - frac * chartH;
						return (
							<g key={frac}>
								<line
									x1={padding.left}
									y1={y}
									x2={padding.left + chartW}
									y2={y}
									stroke="#F3F4F6"
									strokeWidth="1"
								/>
								<text
									x={padding.left - 6}
									y={y + 4}
									textAnchor="end"
									fontSize="10"
									fill="#9CA3AF"
								>
									{Math.round(frac * maxCount)}
								</text>
							</g>
						);
					})}
					{/* Barras */}
					{buckets.map((b, i) => {
						const x = padding.left + i * (barW + barGap);
						const totalH = scale(b.completed + b.failed);
						const completedH = scale(b.completed);
						const failedH = scale(b.failed);
						const yCompleted = padding.top + chartH - completedH;
						const yFailed = padding.top + chartH - totalH;
						const showLabel = i % 5 === 0 || i === buckets.length - 1;
						return (
							<g key={b.day}>
								{b.failed > 0 && (
									<rect
										x={x}
										y={yFailed}
										width={barW}
										height={failedH}
										fill="#F87171"
									/>
								)}
								{b.completed > 0 && (
									<rect
										x={x}
										y={yCompleted}
										width={barW}
										height={completedH}
										fill="#4ADE80"
									/>
								)}
								<title>
									{b.day} — {b.completed} completados
									{b.failed > 0 ? `, ${b.failed} fallidos` : ''}
								</title>
								{showLabel && (
									<text
										x={x + barW / 2}
										y={padding.top + chartH + 14}
										textAnchor="middle"
										fontSize="9"
										fill="#9CA3AF"
									>
										{b.day.slice(5)}
									</text>
								)}
							</g>
						);
					})}
				</svg>
			</Box>
		</Box>
	);
}

// ----------------------------------------------------------------------------
// BatchDetailModal — modal con toda la info del batch: header con stats,
// cada item con metadata expandida (video_id, URL, voz, duración, tamaño, etc.)
// del payload_snapshot.
// ----------------------------------------------------------------------------

function formatBytes(n) {
	if (!n || Number.isNaN(n)) return '—';
	const mb = n / (1024 * 1024);
	if (mb < 1) return `${(n / 1024).toFixed(0)} KB`;
	return `${mb.toFixed(2)} MB`;
}

function formatDuration(s) {
	if (!s || Number.isNaN(s)) return '—';
	const mins = Math.floor(s / 60);
	const secs = Math.round(s % 60);
	return `${mins}:${String(secs).padStart(2, '0')}`;
}

function BatchDetailModal({ isOpen, onClose, batch, items, loading, onRetry, onRegenerated }) {
	if (!batch) return null;
	const failed = items ? items.failed : batch.failed;
	const completed = items ? items.completed : batch.completed;
	const total = items ? items.total : batch.total;
	const pctComplete = total > 0 ? Math.round((completed / total) * 100) : 0;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent maxH="90vh">
				<ModalHeader>
					<Flex align="center" gap="12px" wrap="wrap">
						<Text fontSize="16px">Detalle del batch</Text>
						<Badge colorScheme={batch.type === 'hipercompartidos' ? 'yellow' : 'purple'}>
							{batch.type === 'hipercompartidos' ? 'Prioritario' : 'Programa'}
						</Badge>
						<Text
							fontSize="12px"
							fontFamily="mono"
							color="gray.500"
							fontWeight="400"
						>
							{batch.batch_id}
						</Text>
					</Flex>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Grid templateColumns="repeat(4, 1fr)" gap="10px" mb="14px">
						<KpiCard label="Total videos" value={String(total)} sub={`${pctComplete}% ok`} />
						<KpiCard label="Completados" value={String(completed)} sub="✓ callback OK" />
						<KpiCard label="Fallidos" value={String(failed)} sub={failed > 0 ? 'requieren reintento' : 'ninguno'} />
						<KpiCard
							label="Costo estimado"
							value={`USD $${batch.cost_estimated_usd.toFixed(2)}`}
							sub={`USD $${COST_PER_VIDEO_USD.toFixed(2)}/video`}
						/>
					</Grid>

					<Progress
						value={pctComplete}
						colorScheme={failed > 0 ? 'orange' : 'green'}
						size="sm"
						borderRadius="4px"
						mb="16px"
						bg="gray.100"
					/>

					{loading && <Skeleton height="120px" borderRadius="6px" />}
					{!loading && items && items.items.length === 0 && (
						<Text fontSize="13px" color="gray.500" textAlign="center" py="20px">
							Este batch no tiene items registrados aún.
						</Text>
					)}
					{!loading &&
						items &&
						items.items.map((it) => (
							<BatchItemDetailCard
								key={it.job_id}
								item={it}
								batchId={batch.batch_id}
								onRegenerated={onRegenerated}
							/>
						))}
				</ModalBody>
				<ModalFooter>
					{failed > 0 && (
						<Button colorScheme="orange" mr="auto" onClick={onRetry}>
							Reintentar {failed} fallido{failed !== 1 ? 's' : ''}
						</Button>
					)}
					<Button variant="ghost" onClick={onClose}>
						Cerrar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

function BatchItemDetailCard({ item, batchId, onRegenerated }) {
	const isFailed = item.status === 'failed';
	const cat = isFailed ? classifyError({ retriable: item.retriable, stage: item.stage }) : null;
	const snap = item.payload_snapshot || {};
	const videoInfo = snap.video || {};
	const genInfo = snap.generation || {};
	const regenModal = useDisclosure();

	return (
		<Box
			border="1px"
			borderColor={isFailed ? 'red.100' : 'gray.200'}
			borderRadius="8px"
			bg={isFailed ? 'red.50' : 'white'}
			p="12px 14px"
			mb="10px"
		>
			<Flex align="center" gap="10px" mb="8px" wrap="wrap">
				{isFailed ? (
					<Badge colorScheme="red">✕ Fallido</Badge>
				) : (
					<Badge colorScheme="green">✓ Completado</Badge>
				)}
				<Text fontWeight="600" fontSize="14px" color="gray.800">
					{item.material_title || `Material #${item.material_id}`}
				</Text>
				{isFailed && cat && (
					<Badge colorScheme={cat.color} fontSize="10px">
						{cat.label}
					</Badge>
				)}
				{isFailed && item.stage && (
					<Badge colorScheme="gray" fontSize="10px">
						Etapa: {STAGE_LABEL[item.stage] || item.stage}
					</Badge>
				)}
				<Text fontSize="11px" color="gray.500" ml="auto">
					{timeAgo(item.processed_at)}
				</Text>
			</Flex>

			<Grid templateColumns="repeat(3, 1fr)" gap="8px 20px" fontSize="12px">
				<InfoRow label="Material ID" value={`#${item.material_id}`} />
				<InfoRow label="Video ID" value={item.video_id != null ? `#${item.video_id}` : '—'} />
				<InfoRow label="Job ID" value={item.job_id} mono />

				<InfoRow
					label="Programa"
					value={item.program ? item.program.programa : '—'}
				/>
				<InfoRow label="Materia" value={item.program ? item.program.materia : '—'} />
				<InfoRow label="Unidad" value={item.unidad || '—'} />

				{!isFailed && (
					<>
						<InfoRow label="Voz usada" value={genInfo.voice_id || '—'} />
						<InfoRow
							label="Duración"
							value={formatDuration(videoInfo.duration_seconds)}
						/>
						<InfoRow label="Tamaño" value={formatBytes(videoInfo.size_bytes)} />
					</>
				)}

				{item.estudiantes != null && (
					<InfoRow
						label="Estudiantes activos"
						value={item.estudiantes.toLocaleString()}
					/>
				)}
			</Grid>

			{!isFailed && videoInfo.source && (
				<Box mt="10px" pt="10px" borderTop="1px" borderColor="gray.100">
					<Text fontSize="11px" color="gray.500" mb="2px">
						URL del video generado
					</Text>
					<Link
						href={videoInfo.source}
						isExternal
						fontSize="11px"
						fontFamily="mono"
						color="purple.600"
						wordBreak="break-all"
					>
						{videoInfo.source} ↗
					</Link>
				</Box>
			)}

			{isFailed && (item.error_code || item.error_message) && (
				<Box mt="10px" pt="10px" borderTop="1px" borderColor="red.100">
					{item.error_code && (
						<Text fontSize="12px" color="red.700" fontWeight="600">
							{item.error_code}
						</Text>
					)}
					{item.error_message && (
						<Text
							fontSize="11px"
							color="red.600"
							fontFamily="mono"
							whiteSpace="pre-wrap"
							mt="2px"
						>
							{item.error_message}
						</Text>
					)}
				</Box>
			)}

			<Flex mt="10px" pt="10px" borderTop="1px" borderColor="gray.100" justify="flex-end">
				<Button
					size="xs"
					variant="ghost"
					colorScheme="purple"
					onClick={regenModal.onOpen}
				>
					Regenerar con instrucciones
				</Button>
			</Flex>

			<RegenerateItemModal
				isOpen={regenModal.isOpen}
				onClose={regenModal.onClose}
				batchId={batchId}
				item={item}
				currentVoice={genInfo.voice_id || null}
				onRegenerated={onRegenerated}
			/>
		</Box>
	);
}

function InfoRow({ label, value, mono }) {
	return (
		<Box>
			<Text fontSize="10px" color="gray.500" textTransform="uppercase">
				{label}
			</Text>
			<Text
				fontSize="12px"
				color="gray.800"
				fontFamily={mono ? 'mono' : undefined}
				wordBreak="break-all"
			>
				{value}
			</Text>
		</Box>
	);
}


function ReintentarModal({ isOpen, onClose, batch, onConfirm }) {
	const [voicePreset, setVoicePreset] = React.useState('');
	const [submitting, setSubmitting] = React.useState(false);

	React.useEffect(() => {
		if (isOpen) {
			setVoicePreset('');
			setSubmitting(false);
		}
	}, [isOpen]);

	if (!batch) return null;

	const cost = (batch.failed * COST_PER_VIDEO_USD).toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});
	const tiempo = Math.round(batch.failed * 7);

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			await onConfirm(voicePreset);
		} catch {
			setSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Reintentar fallidos</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Text fontSize="13px" color="gray.700" mb="6px">
						Batch original: <b>{batch.batch_id}</b>
					</Text>
					<Text fontSize="13px" color="gray.600" mb="14px">
						Se van a reintentar <b>{batch.failed} videos fallidos</b>. Genera un nuevo
						batch_id (sufijo <code>-retry-XXXX</code>) — el original queda como referencia
						histórica.
					</Text>

					<Box bg="orange.50" border="1px" borderColor="orange.200" borderRadius="6px" p="10px 12px" mb="12px">
						<Text fontSize="12px" fontWeight="600" color="orange.800">
							Advertencia — el reintento genera costo real
						</Text>
						<Text fontSize="12px" color="orange.700" mt="4px">
							Cada video reintentado ejecuta el pipeline completo (LLM + TTS + render) —
							no reutiliza el trabajo del fallo previo. Si el fallo fue determinístico
							(schema inválido, tema imposible), el reintento va a fallar igual.
						</Text>
					</Box>

					<FormControl>
						<FormLabel fontSize="12px" color="gray.600" mb="4px">
							Voz para el reintento
						</FormLabel>
						<Select
							size="sm"
							value={voicePreset}
							onChange={(e) => setVoicePreset(e.target.value)}
							bg="white"
						>
							{VOICE_PRESET_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</FormControl>

					<Flex mt="14px" gap="16px" fontSize="12px" color="gray.700">
						<Box>
							Costo estimado: <b>{cost}</b>
						</Box>
						<Box>
							Tiempo aprox: <b>~{tiempo} min</b>
						</Box>
					</Flex>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
						Cancelar
					</Button>
					<Button colorScheme="orange" onClick={handleConfirm} isLoading={submitting}>
						Confirmar y reintentar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

// ----------------------------------------------------------------------------
// RegenerateItemModal — Fase 10.8
//
// Regenera UN video del batch pasando instrucciones libres al LLM. El item
// puede estar completado o fallido — el punto es refinar un resultado que
// salió pobre (ej. video del alphabet que solo mostró 5 letras).
//
// Sobrescribe el video en su path original (Fase 10.7) — sin rollback.
// El warning es lo suficientemente explícito para dejarlo claro.
// ----------------------------------------------------------------------------
// Modelos disponibles para el A/B testing. El default vacío = usa env.LLM_MODEL_SCRIPT.
// Sonnet 5 intro pricing hasta 2026-08-31 — pero en la práctica gasta más
// output tokens (thinking adaptive extendido) y pierde calidad visual → no es
// más barato en este pipeline. Opus 4.7 tiene mismo pricing que 4.8; se prueba
// para ver si razona con menos tokens en tareas estructuradas de SVG.
const LLM_MODEL_OPTIONS = [
	{ value: '', label: 'Default (Opus 4.8) — máxima calidad · ~$0.48/video' },
	{ value: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro (test A/B) — ~$0.04/video · calidad por validar' },
	{ value: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash (test A/B) — ~$0.01/video · el más económico' },
	{ value: 'claude-opus-4-7', label: 'Opus 4.7 (test A/B) — mismo pricing que 4.8, distinto razonamiento' },
	{ value: 'claude-sonnet-5', label: 'Sonnet 5 (intro) — más caro en tokens de thinking, calidad menor observada' },
	{ value: 'claude-opus-4-8', label: 'Opus 4.8 (explícito)' },
];

// Costo LLM estimado por modelo para el footer del modal de regeneración.
const LLM_MODEL_COST_LABEL = {
	'': 'USD $0.48',
	'claude-opus-4-8': 'USD $0.48',
	'claude-opus-4-7': 'USD $0.48',
	'claude-sonnet-5': 'USD $0.55',
	'deepseek-v4-pro': 'USD $0.04',
	'deepseek-v4-flash': 'USD $0.01',
};

function RegenerateItemModal({ isOpen, onClose, batchId, item, currentVoice, onRegenerated }) {
	const { trackBatch } = useRescueBatches();
	const [instruction, setInstruction] = React.useState('');
	const [voicePreset, setVoicePreset] = React.useState('');
	const [llmModel, setLlmModel] = React.useState('');
	const [submitting, setSubmitting] = React.useState(false);
	const toast = useToast();

	React.useEffect(() => {
		if (isOpen) {
			setInstruction('');
			setVoicePreset('');
			setLlmModel('');
			setSubmitting(false);
		}
	}, [isOpen]);

	if (!item) return null;

	const trimmed = instruction.trim();
	// Instrucciones son opcionales: el flujo también sirve para A/B testear
	// modelos (Sonnet 5 vs Opus 4.8) sin cambiar el prompt. Solo bloqueamos
	// si excede el largo máximo o si el submit está en curso.
	const canSubmit = trimmed.length <= 2000 && !submitting;
	const charCountColor = trimmed.length > 2000 ? 'red.600' : 'gray.500';
	const cost = COST_PER_VIDEO_USD.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
	});

	const handleConfirm = async () => {
		if (!canSubmit) return;
		setSubmitting(true);
		try {
			const res = await regenerateBatchItem(batchId, item.material_id, {
				customInstruction: trimmed || undefined,
				voicePreset: voicePreset || undefined,
				llmModel: llmModel || undefined,
			});
			// eslint-disable-next-line no-console
			console.log('[regenerate] backend response:', res);
			const jobsCount = res.worker_response?.jobs?.length ?? 0;
			// Track del batch de regen para que el LiveBatchStatusPanel aparezca
			// arriba del dashboard con el progreso en vivo (mismo panel que se
			// muestra para batches encolados desde Recuperar). Solo si el
			// worker realmente encoló al menos 1 job — así evitamos chips
			// zombies cuando el batch falló silenciosamente en el worker.
			if (res.regen_batch_id && jobsCount > 0) {
				trackBatch({
					batch_id: res.regen_batch_id,
					encolados: jobsCount,
					kind: 'regen',
					label: `Regeneración · ${item.material_title || `Material #${item.material_id}`}${
						llmModel ? ` · ${llmModel}` : ''
					}`,
					started_at: new Date().toISOString(),
				});
			}
			toast({
				title: 'Regeneración encolada',
				description: (
					<>
						<div>ID nuevo batch:</div>
						<code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
							{res.regen_batch_id || '(NO devuelto)'}
						</code>
						<div style={{ marginTop: 6, fontSize: 11 }}>
							Jobs encolados en worker: <b>{jobsCount}</b>
							{jobsCount > 0 && ' · el progreso aparece arriba del listado'}
						</div>
					</>
				),
				status: res.regen_batch_id && jobsCount > 0 ? 'success' : 'warning',
				duration: 10000,
			});
			onClose();
			if (onRegenerated) {
				await onRegenerated({ regen_batch_id: res.regen_batch_id, material_id: item.material_id });
			}
		} catch (err) {
			const body = err.response && err.response.data ? err.response.data : {};
			toast({
				title: 'No se pudo regenerar',
				description: body.message || err.message,
				status: 'error',
				duration: 8000,
			});
			setSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered size="xl" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Regenerar con instrucciones</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Text fontSize="13px" color="gray.600" mb="10px">
						Material <b>#{item.material_id}</b>
						{item.video_id != null && (
							<> · Video <b>#{item.video_id}</b></>
						)}
						{item.material_title && (
							<> · <b>{item.material_title}</b></>
						)}
					</Text>
					{item.program && (
						<Text fontSize="12px" color="gray.500" mb="14px">
							{item.program.programa} · {item.program.materia}
							{item.unidad ? ` · ${item.unidad}` : ''}
							{currentVoice ? ` · voz actual: ${currentVoice}` : ''}
						</Text>
					)}

					<Box bg="red.50" border="1px" borderColor="red.200" borderRadius="6px" p="10px 12px" mb="14px">
						<Text fontSize="12px" fontWeight="600" color="red.800">
							⚠ Esto reemplazará el video actual en el CDN
						</Text>
						<Text fontSize="12px" color="red.700" mt="4px">
							El nuevo video se sube al mismo path que el actual, así que la
							versión anterior se pierde y no hay rollback. Si el resultado
							tampoco es satisfactorio, vas a tener que regenerar de nuevo
							(otro cargo).
						</Text>
					</Box>

					<FormControl mb="12px">
						<FormLabel fontSize="12px" color="gray.700" mb="4px">
							Indicaciones para el modelo
						</FormLabel>
						<Input
							as="textarea"
							value={instruction}
							onChange={(e) => setInstruction(e.target.value)}
							placeholder={
								'Ej: cubre las 26 letras del alfabeto, no solo una selección. Para cada letra, incluye su pronunciación fonética con un ejemplo de palabra que empiece con ella. El ritmo debe permitir repetir cada letra en voz alta.'
							}
							minH="120px"
							bg="white"
							fontSize="13px"
							p="10px 12px"
							resize="vertical"
						/>
						<Flex justify="space-between" mt="4px">
							<Text fontSize="11px" color="gray.500">
								Opcional (máx 2000). Sé específico — el modelo tratará esto como restricción prioritaria. Dejalo vacío si solo querés probar otro modelo LLM.
							</Text>
							<Text fontSize="11px" color={charCountColor}>
								{trimmed.length}/2000
							</Text>
						</Flex>
					</FormControl>

					<FormControl mb="12px">
						<FormLabel fontSize="12px" color="gray.700" mb="4px">
							Voz (opcional — deja "por defecto" para mantener la del batch)
						</FormLabel>
						<Select
							size="sm"
							value={voicePreset}
							onChange={(e) => setVoicePreset(e.target.value)}
							bg="white"
						>
							{VOICE_PRESET_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
					</FormControl>

					<FormControl mb="4px">
						<FormLabel fontSize="12px" color="gray.700" mb="4px">
							Modelo LLM (A/B testing)
						</FormLabel>
						<Select
							size="sm"
							value={llmModel}
							onChange={(e) => setLlmModel(e.target.value)}
							bg="white"
						>
							{LLM_MODEL_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</Select>
						<Text fontSize="10px" color="gray.500" mt="4px">
							Sonnet 5 mantiene el mismo pipeline y schema — solo cambia el modelo
							que genera el guion. Después podés comparar los MP4 lado a lado.
						</Text>
					</FormControl>

					<Flex mt="14px" gap="16px" fontSize="12px" color="gray.700">
						<Box>
							Costo estimado: <b>{LLM_MODEL_COST_LABEL[llmModel] || cost}</b>
						</Box>
						<Box>
							Tiempo aprox: <b>~7 min</b>
						</Box>
					</Flex>
				</ModalBody>
				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={submitting}>
						Cancelar
					</Button>
					<Button
						colorScheme="purple"
						onClick={handleConfirm}
						isLoading={submitting}
						isDisabled={!canSubmit}
					>
						Regenerar (reemplaza video actual)
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
